import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useOutletContext, useLocation } from "react-router-dom";
import axios from "axios";
import io from "socket.io-client";
import confetti from "canvas-confetti";
import {
  Play,
  ChevronDown,
  ChevronRight,
  Share2,
  Copy,
  Crown,
  Dice5,
  User,
} from "lucide-react";

import Breadcrumb from "../../Breadcrumb/Breadcrumb";
import GameBoard from "../GameBoard/GameBoard";
import Modal from "../../Modal/Modal";
import { motion, AnimatePresence } from "framer-motion";
import "./GameDetail.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/clan`;

const getSocket = () => {
  return io(process.env.REACT_APP_API_BASE_URL);
};

const socket = getSocket();

const getContrastTextColor = (hexColor) => {
  const hex = hexColor.startsWith("#") ? hexColor.slice(1) : hexColor;
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 160 ? "#000000" : "#FFFFFF";
};

const showConfettiFireworks = () => {
  var duration = 15 * 1000;
  var animationEnd = Date.now() + duration;
  var defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

  function randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  var interval = setInterval(function () {
    var timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    var particleCount = 50 * (timeLeft / duration);
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
    });
  }, 250);
};

const getOrdinal = (n) => {
  if (typeof n !== "number") return "N/A";
  if (n > 3 && n < 21) return n + "th";
  switch (n % 10) {
    case 1:
      return n + "st";
    case 2:
      return n + "nd";
    case 3:
      return n + "rd";
    default:
      return n + "th";
  }
};

const formatCountdown = (ms) => {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? "0" + v : v))
    .filter((v, i) => v !== "00" || i > 0)
    .join(":");
};

function GameDetail() {
  const location = useLocation();
  const breadcrumbData = location.state?.breadcrumbFrom;
  const { clanId, eventId, gameId } = useParams();
  const { userData } = useOutletContext();
  const currentUserId = userData?.id || null;

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isEventStarted, setIsEventStarted] = useState(true);
  const [countdownText, setCountdownText] = useState("");

  const [userTeamId, setUserTeamId] = useState(null);
  const [performerIgnId, setPerformerIgnId] = useState(null);
  const [performerIgn, setPerformerIgn] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  const [isRoller, setIsRoller] = useState(false);

  const [initialUserTeamId, setInitialUserTeamId] = useState(null);

  const [gameLog, setGameLog] = useState([]);
  const [isRolling, setIsRolling] = useState(false);
  const [showRollModal, setShowRollModal] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const [isContinuing, setIsContinuing] = useState(false);

  const [expandedTeams, setExpandedTeams] = useState(new Set());

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  const [selectedFilterTeam, setSelectedFilterTeam] = useState("All");

  const [showProgressionLines, setShowProgressionLines] = useState(false);

  const [socketMessage, setSocketMessage] = useState(null);

  const filteredLogs = useMemo(() => {
    if (selectedFilterTeam === "All") return gameLog;
    return gameLog.filter((log) => log.team_name === selectedFilterTeam);
  }, [gameLog, selectedFilterTeam]);

  const handleCopyLink = () => {
    const spectatorUrl = `${window.location.origin}/games/spectate/${gameId}`;
    navigator.clipboard.writeText(spectatorUrl).then(() => {
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 3000);
    });
  };

  const processUserMembership = useCallback((userTeams, authUserId) => {
    const authIdStr = String(authUserId);

    if (!userTeams || authIdStr === "null" || authIdStr === "undefined") {
      setUserTeamId(null);
      setPerformerIgnId(null);
      setPerformerIgn(null);
      setIsLeader(false);
      setIsRoller(false);
      return;
    }

    const userMembership = userTeams.find(
      (member) =>
        String(member.osrs_ign_id || "") === authIdStr ||
        String(member.user_id || "") === authIdStr,
    );

    if (userMembership) {
      const isUserLeader = userMembership.role === "Leader";
      const isUserRoller = userMembership.role === "Roller";
      const teamId = userMembership.team_id || userMembership.id;

      setUserTeamId(teamId);
      setPerformerIgnId(userMembership.osrs_ign_id);
      setPerformerIgn(userMembership.player_ign);
      setIsLeader(isUserLeader);
      setIsRoller(isUserRoller);
      setInitialUserTeamId(teamId);
    } else {
      setUserTeamId(null);
      setPerformerIgnId(null);
      setPerformerIgn(null);
      setIsLeader(false);
      setIsRoller(false);
    }
  }, []);

  const isClanAdmin = useMemo(() => {
    if (!userData?.clan_memberships_list || !clanId) return false;

    const membership = userData.clan_memberships_list.find(
      (m) => String(m.clan_id) === String(clanId),
    );

    return (
      membership &&
      (membership.role === "Admin" ||
        membership.role === "Owner")
    );
  }, [userData, clanId]);

  const fetchGameDetails = useCallback(async () => {
    setLoading(true);
    try {
      const gameUrl = `${API_BASE_URL}/${clanId}/events/${eventId}/game/${gameId}`;
      const gameResponse = await axios.get(gameUrl, {
        withCredentials: true,
      });
      let gameData = gameResponse.data.game;
      setGame(gameData);
      setGameLog(gameData.log_entries || []);
      if (currentUserId !== null) {
        processUserMembership(gameData.user_teams, currentUserId);
      }
    } catch (err) {
      setError(
        "Failed to load game details. It may not exist or the link is invalid.",
      );
    } finally {
      setLoading(false);
    }
  }, [clanId, eventId, gameId, currentUserId, processUserMembership]);

  useEffect(() => {
    if (clanId && eventId && gameId) {
      fetchGameDetails();
    }
  }, [fetchGameDetails, clanId, eventId, gameId]);

  useEffect(() => {
    if (game?.user_teams && currentUserId !== null) {
      processUserMembership(game.user_teams, currentUserId);
    }
  }, [game?.user_teams, currentUserId, processUserMembership]);

  useEffect(() => {
    if (!gameId || currentUserId === null) return;

    socket.emit("join_game", { game_id: gameId });

    const handleGameStateUpdate = (data) => {
      setGame((prevGame) => {
        if (!prevGame) return null;

        const incomingGame = data.game_state;
        if (!incomingGame) return prevGame;

        const updatedGame = {
          ...prevGame,
          ...incomingGame,
        };

        if (incomingGame.user_teams && incomingGame.user_teams.length > 0) {
          updatedGame.user_teams = incomingGame.user_teams;
        } else if (prevGame.user_teams) {
          updatedGame.user_teams = prevGame.user_teams;
        } else {
          updatedGame.user_teams = [];
        }

        setIsRolling(false);
        setIsReverting(false);
        setIsContinuing(false);

        return updatedGame;
      });
      setShowRevertModal(false);
    };

    const handleNewLogEntry = (data) => {
      setGameLog((prevLog) => [...prevLog, data.log_entry]);
    };

    const handleGameActionPermissions = (data) => {
      const updatedTeam = data.updated_team_data;
      if (!updatedTeam) return;

      setGame((prevGame) => {
        if (!prevGame) return null;

        let newUserTeams = [...(prevGame.user_teams || [])];
        const currentUserIdStr = String(currentUserId);

        const userIsMemberInUpdatedTeam = updatedTeam.members.find(
          (member) => String(member.osrs_ign_id) === currentUserIdStr,
        );

        const existingIndex = newUserTeams.findIndex(
          (member) => String(member.osrs_ign_id) === currentUserIdStr,
        );

        if (userIsMemberInUpdatedTeam) {
          const newMember = {
            id: updatedTeam.id,
            name: updatedTeam.name,
            color: updatedTeam.color,
            player_ign: userIsMemberInUpdatedTeam.osrs_ign,
            osrs_ign_id: userIsMemberInUpdatedTeam.osrs_ign_id,
            role: userIsMemberInUpdatedTeam.role,
            team_id: updatedTeam.id,
          };

          if (existingIndex > -1) {
            newUserTeams[existingIndex] = newMember;
          } else {
            newUserTeams.push(newMember);
          }
        } else if (existingIndex > -1) {
          newUserTeams.splice(existingIndex, 1);
        }

        return {
          ...prevGame,
          user_teams: newUserTeams,
        };
      });
    };

    const handleSocketError = (data) => {
      setSocketMessage({ type: "error", text: data.message });
      fetchGameDetails();
      setTimeout(() => setSocketMessage(null), 5000);
    };

    const handleRevertError = (data) => {
      setIsReverting(false);
      setShowRevertModal(false);
      setSocketMessage({ type: "error", text: data.message });
      fetchGameDetails();
      setTimeout(() => setSocketMessage(null), 5000);
    };
    socket.on("game_state_update", handleGameStateUpdate);
    socket.on("tasks_reordered", handleGameStateUpdate);
    socket.on("new_log_entry", handleNewLogEntry);
    socket.on("team_update", handleGameActionPermissions);
    socket.on("roll_error", handleSocketError);
    socket.on("revert_error", handleRevertError);

    return () => {
      socket.emit("leave_game", { game_id: gameId });
      socket.off("game_state_update", handleGameStateUpdate);
      socket.off("tasks_reordered", handleGameStateUpdate);
      socket.off("new_log_entry", handleNewLogEntry);
      socket.off("team_update", handleGameActionPermissions);
      socket.off("roll_error", handleSocketError);
      socket.off("revert_error", handleRevertError);
    };
  }, [gameId, currentUserId]);

  useEffect(() => {
    if (!game?.start_datetime) return;

    const updateTimer = () => {
      const dateString =
        game.start_datetime.endsWith("Z") || game.start_datetime.includes("+")
          ? game.start_datetime
          : `${game.start_datetime}Z`;

      const startTime = new Date(dateString).getTime();
      const now = new Date().getTime();
      const diff = startTime - now;

      if (diff <= 0) {
        setIsEventStarted(true);
        setCountdownText("");
      } else {
        setIsEventStarted(false);
        setCountdownText(formatCountdown(diff));
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [game?.start_datetime]);

  let winnersDataList = [];
  try {
    if (game?.winners) {
      const parsedWinners = JSON.parse(game.winners);
      winnersDataList = Array.isArray(parsedWinners)
        ? parsedWinners
        : parsedWinners
          ? [parsedWinners]
          : [];
    }
  } catch (e) {
    console.error("Error parsing game.winners JSON:", e);
  }

  const currentWinnerCount = winnersDataList.length;
  const maxRanks = game?.max_teams || game?.team_states?.length || 0;
  const latestWinnerData = winnersDataList[currentWinnerCount - 1] || null;
  const gameCompleted = game?.status === "completed";
  const showContinueGameButton =
    (game?.status === "finished" || game?.status === "in-progress") &&
    currentWinnerCount < maxRanks &&
    latestWinnerData;

  const hasUserTeamWon = winnersDataList.some(
    (winner) => String(winner.id) === String(initialUserTeamId),
  );

  useEffect(() => {
    if (currentWinnerCount > 0) {
      showConfettiFireworks();
    }
  }, [currentWinnerCount]);

  const handleRollDice = () => {
    if (
      !userTeamId ||
      !performerIgnId ||
      isRolling ||
      isReverting ||
      isReordering ||
      !(isLeader || isRoller) ||
      hasUserTeamWon
    )
      return;

    setShowRollModal(true);
  };

  const confirmRoll = () => {
    setIsRolling(true);
    setShowRollModal(false);

    const timer = setTimeout(() => {
      setIsRolling(false);
      console.warn("Dice roll request timed out.");
    }, 5000);

    socket.emit("roll_dice", {
      game_id: gameId,
      team_id: userTeamId,
      performer_ign_id: performerIgnId,
    });
  };

  const handleRevertRoll = () => {
    if (
      !userTeamId ||
      !performerIgnId ||
      isRolling ||
      isReverting ||
      isReordering ||
      !isLeader
    )
      return;

    setShowRevertModal(true);
  };

  const confirmRevert = () => {
    if (
      !userTeamId ||
      !performerIgnId ||
      isRolling ||
      isReverting ||
      isReordering ||
      !isLeader
    )
      return;

    setIsReverting(true);

    const revertTimeout = setTimeout(() => {
      if (isReverting) {
        setIsReverting(false);
        setShowRevertModal(false);
        setSocketMessage({ type: "error", text: "Revert request timed out." });
      }
    }, 5000);

    socket.emit("revert_roll", {
      game_id: gameId,
      team_id: userTeamId,
      performer_ign_id: performerIgnId,
    });
  };

  const handleContinueGame = async () => {
    if (
      !currentUserId ||
      (hasUserTeamWon && !isClanAdmin) ||
      isContinuing ||
      isRolling ||
      isReverting ||
      isReordering ||
      !showContinueGameButton
    ) {
      return;
    }

    setIsContinuing(true);
    setError(null);

    const continueUrl = `${API_BASE_URL}/${clanId}/events/${eventId}/game/${gameId}/continue`;
    const payload = {
      performer_ign_id: performerIgnId || null,
      team_id: userTeamId || null,
    };

    try {
      await axios.put(continueUrl, payload, {
        withCredentials: true,
      });
    } catch (err) {
      setIsContinuing(false);
      const message =
        err.response?.data?.error || "Failed to continue game phase.";
      setError(message);
      fetchGameDetails();
    }
  };

  if (loading)
    return (
      <div className="game-status game-loading-message">
        Loading Game {gameId}...
      </div>
    );
  if (error) return <div className="game-status error-message">{error}</div>;
  if (!game)
    return <div className="game-status not-found">Game not found.</div>;

  const currentTitle = game.event_name || `Game ${gameId}`;
  const gameType = game.event_type || "Game";

  const getStatusClass = (status) => {
    if (!status) return "";
    return `status-${status.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
  };

  const currentUserTeamState = game.team_states.find(
    (state) => state.team_id === userTeamId,
  );

  const revertsRemaining = currentUserTeamState
    ? game.max_reverts_allowed - currentUserTeamState.revert_count
    : 0;

  const userTeamColor = currentUserTeamState
    ? currentUserTeamState.team_color
    : "#333";

  const currentPosition = currentUserTeamState
    ? currentUserTeamState.current_position
    : 0;

  const canRevert = currentPosition > 1 && revertsRemaining > 0 && isLeader;

  const canPerformAction =
    userTeamId &&
    performerIgnId &&
    (isLeader || isRoller) &&
    (isEventStarted || isClanAdmin) &&
    !isReordering &&
    !hasUserTeamWon &&
    game.status !== "paused";

  const getRoleLabel = () => {
    if (isLeader) return "Leader";
    if (isRoller) return "Roller";
    return "Member";
  };

  const rollModalContent = (
    <div className="modal-text-content">
      <p>Are you ready to roll for:</p>

      <div
        className="modal-team-highlight-box"
        style={{
          borderLeftColor: currentUserTeamState?.team_color || "#3498db",
        }}
      >
        <span
          className="modal-team-name"
          style={{ color: currentUserTeamState?.team_color || "#3498db" }}
        >
          {currentUserTeamState?.team_name}
        </span>
      </div>

      <p style={{ fontSize: "0.95rem", lineHeight: "1.4" }}>
        Please ensure you have{" "}
        <strong>verified the completed tile with a screenshot</strong> meeting
        all criteria before rolling.
      </p>
      <p style={{ fontSize: "0.95rem", color: "#ccc", marginTop: "10px" }}>
        Also, make sure the drop counts for the tile by checking the{" "}
        <strong>exclusions section</strong> of the rules.
      </p>
    </div>
  );

  const revertModalContent = (
    <div className="modal-text-content">
      <p>Are you sure you want to revert the last roll for:</p>

      <div
        className="modal-team-highlight-box"
        style={{
          borderLeftColor: currentUserTeamState?.team_color || "#e74c3c",
        }}
      >
        <span
          className="modal-team-name"
          style={{ color: currentUserTeamState?.team_color || "#e74c3c" }}
        >
          {currentUserTeamState?.team_name}
        </span>
      </div>

      <div style={{ marginTop: "15px" }}>
        <p>
          This action will undo the last movement and consume one of your team's
          revert tokens.
        </p>
        <hr style={{ margin: "15px 0", borderColor: "#444" }} />
        <p style={{ fontSize: "1.1rem" }}>
          <strong>Reverts remaining: {revertsRemaining}</strong>
        </p>
      </div>
    </div>
  );

  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  return (
    <div className="game-detail">
      <Breadcrumb
        previousPageUrl={breadcrumbData?.path}
        previousPageName={breadcrumbData?.text || "Games"}
        currentPageName={currentTitle}
      />
      <hr />
      <div className="game-header-container">
        <h2>
          <span className="clan-prefix">{game.clan_name}</span>
          <span className="title-separator"> / </span>
          {gameType}: {currentTitle}
          <button
            className="btn-share-link"
            onClick={handleCopyLink}
            title="Share Game With Friends"
          >
            <Share2 size={20} />
          </button>
        </h2>
      </div>

      <div className="game-status-info">
        <p className="game-summary game-summary-text">
          Status:&nbsp;
          <strong
            className={`game-status-badge ${!isEventStarted && game.status === "in-progress" ? "status-upcoming" : getStatusClass(game.status)}`}
          >
            {!isEventStarted && game.status === "in-progress"
              ? "UPCOMING"
              : game.status}
          </strong>
          &nbsp;| Board Size: <strong>{game.board_size}</strong>
        </p>

        {/* Countdown Section */}
        {!isEventStarted &&
          game.status !== "completed" &&
          game.status !== "finished" && (
            <div className="event-timer-container">
              <div className="countdown-timer-text">
                Starts in: {countdownText}
              </div>
              <span className="local-start-time">
                Your Local Time:{" "}
                {new Date(game.start_datetime + "Z").toLocaleString([], {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </span>
            </div>
          )}

        {/* LIVE Indicator Section */}
        {isEventStarted && game.status === "in-progress" && (
          <div className="live-indicator">
            <span className="pulse-dot">●</span> LIVE
          </div>
        )}
      </div>

      <div className="game-main-content">
        <div className="game-board-container">
          <GameBoard
            game={game}
            selectedFilterTeam={selectedFilterTeam}
            showProgressionLines={showProgressionLines}
          />
        </div>

        <div className="game-sidebar">
          <div className="dice-roll-ui">
            {latestWinnerData && !gameCompleted && (
              <p
                className="game-status-message winner-message latest-winner-announcement"
                style={{
                  backgroundColor: latestWinnerData.color || "#27ae60",
                  color: getContrastTextColor(
                    latestWinnerData.color || "#27ae60",
                  ),
                }}
              >
                🎉 Rank Secured! Congratulations to the{" "}
                <strong>{getOrdinal(latestWinnerData.rank)} Place</strong>{" "}
                Winner:{" "}
                <strong className="latest-winner-name">
                  Team {latestWinnerData.name}
                </strong>
                !
              </p>
            )}

            <h3>Game Action</h3>

            {gameCompleted ? (
              <p className="game-status-message status-completed winner-message">
                Game Over! All final ranks have been secured.
              </p>
            ) : !gameCompleted && currentWinnerCount < maxRanks ? (
              <>
                {game.status === "finished" ? (
                  <div className="continue-game-wrapper">
                    {hasUserTeamWon && !isClanAdmin ? (
                      <p className="game-status-message winner-message team-won-status">
                        Your team has already secured a rank (
                        <strong>
                          {getOrdinal(
                            winnersDataList.find(
                              (w) => String(w.id) === String(initialUserTeamId),
                            )?.rank,
                          )}
                        </strong>
                        ) and can no longer perform actions in this game phase.
                      </p>
                    ) : (
                      <>
                        <h3>Next Rank Competition</h3>
                        <p>
                          The game is currently paused at{" "}
                          <strong>{getOrdinal(currentWinnerCount)}</strong>.
                          Continue the game to allow remaining teams to compete
                          for the next rank (
                          <strong>{getOrdinal(currentWinnerCount + 1)}</strong>
                          ).
                        </p>
                        <button
                          className="btn-success continue-btn"
                          onClick={handleContinueGame}
                          disabled={
                            isContinuing ||
                            !currentUserId ||
                            isRolling ||
                            isReverting ||
                            isReordering ||
                            !(isClanAdmin || isLeader)
                          }
                        >
                          <Play
                            size={18}
                            style={{
                              marginRight: "5px",
                              verticalAlign: "text-bottom",
                            }}
                          />
                          {isContinuing ? "Continuing..." : "Continue Game"}
                        </button>

                        {currentUserId && (
                          <p className="info-message">
                            {isClanAdmin ? (
                              <span className="success-message">
                                <strong>Clan Admin Override:</strong> You can
                                manage game phases.
                              </span>
                            ) : !isLeader ? (
                              <span className="warning-message">
                                Only <strong>Team Leaders</strong> or{" "}
                                <strong>Clan Admins</strong> can continue the
                                game.
                              </span>
                            ) : (
                              <>
                                As <strong>Team Leader</strong>, you can advance
                                the game to the next rank.
                              </>
                            )}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {hasUserTeamWon ? (
                      <p className="game-status-message winner-message team-won-status">
                        Your team has already secured a rank (
                        <strong>
                          {getOrdinal(
                            winnersDataList.find(
                              (w) => String(w.id) === String(initialUserTeamId),
                            )?.rank,
                          )}
                        </strong>
                        ) and can no longer participate in this game phase.
                      </p>
                    ) : (
                      <>
                        <p>
                          The game is open-roll. Only the{" "}
                          <strong>Team Leader</strong> can roll and revert
                          turns.
                        </p>

                        <div className="button-container">
                          <button
                            className="btn-primary"
                            onClick={handleRollDice}
                            disabled={
                              !canPerformAction ||
                              isRolling ||
                              isReverting ||
                              isReordering
                            }
                          >
                            {isRolling
                              ? "Rolling..."
                              : isReordering
                                ? "Wait for Reorder..."
                                : game.status === "paused"
                                  ? "Game Paused"
                                  : !isEventStarted && !isClanAdmin
                                    ? "Waiting for Start..."
                                    : canPerformAction
                                      ? !isEventStarted && isClanAdmin
                                        ? "Admin Override: Roll Early"
                                        : "Roll Dice for Your Team"
                                      : !userTeamId
                                        ? "Join a Team to Roll"
                                        : "Action Unavailable"}
                          </button>

                          {userTeamId && (
                            <button
                              className="btn-secondary"
                              onClick={handleRevertRoll}
                              disabled={
                                isRolling ||
                                isReverting ||
                                isReordering ||
                                !canRevert ||
                                !isLeader
                              }
                            >
                              {isReverting
                                ? "Reverting..."
                                : `Revert Last Roll`}
                            </button>
                          )}
                        </div>

                        <div className="message-container">
                          {socketMessage && (
                            <div className="error-message-box">
                              {socketMessage.text || socketMessage.message}
                            </div>
                          )}

                          {!userTeamId && (
                            <p className="warning-message">
                              You must be a member of a team participating in
                              this game to see game actions.
                            </p>
                          )}

                          {userTeamId && isRoller && (
                            <p className="info-message">
                              Note: As a <strong>Roller</strong>, you can roll
                              the dice but do not have permission to revert
                              turns.
                            </p>
                          )}

                          {userTeamId && !isLeader && !isRoller && (
                            <p className="warning-message">
                              Only the <strong>Leader</strong> or{" "}
                              <strong>Roller</strong> can roll the dice.
                            </p>
                          )}

                          {userTeamId && isLeader && !canRevert && (
                            <p className="warning-message">
                              {currentPosition === 1
                                ? "Revert not allowed: You must be past the starting position (tile 1)."
                                : `Revert not allowed: You have no reverts left (${game.max_reverts_allowed} max).`}
                            </p>
                          )}
                          {userTeamId &&
                            performerIgn &&
                            !isRolling &&
                            !isReverting &&
                            !isReordering && (
                              <p className="info-message">
                                (Rolling as: <strong>{performerIgn}</strong>.
                                Role: <strong>{getRoleLabel()}</strong>
                                .)
                              </p>
                            )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <p
                className={`game-status-message ${getStatusClass(game.status)}`}
              >
                Game is {game.status}. No actions available.
              </p>
            )}
          </div>

          <hr className="sidebar-hr" />

          {currentWinnerCount > 0 && (
            <div className="leaderboard-section">
              <h3>Ranks Secured ({currentWinnerCount})</h3>
              <ul className="team-positions-list">
                {winnersDataList.map((winner) => (
                  <li
                    key={winner.id}
                    className="winner-rank-item"
                    data-rank={winner.rank}
                    style={{ borderLeftColor: winner.color }}
                  >
                    <strong>{getOrdinal(winner.rank)} Place:</strong>
                    <span style={{ color: winner.color }}>{winner.name}</span>
                  </li>
                ))}
              </ul>
              <hr
                className="sidebar-hr"
                style={{ display: "block", margin: "20px 0" }}
              />
            </div>
          )}
          <div className="team-positions-header-container">
            <h3>Team Positions</h3>
            <span className="max-reverts-label">
              (Max reverts allowed: {game.max_reverts_allowed})
            </span>
          </div>
          <ul className="team-positions-list" style={{ position: "relative" }}>
            <AnimatePresence mode="popLayout">
              {[...game.team_states]
                .sort((a, b) => b.current_position - a.current_position)
                .map((state, index, allTeams) => {
                  const isExpanded = expandedTeams.has(state.team_id);
                  const memberCount = state.members?.length || 0;
                  const hasMembers = memberCount > 0;
                  const isAtRevertLimit =
                    state.revert_count >= game.max_reverts_allowed;
                  const dynamicZIndex = allTeams.length - index;

                  const sortedMembers = hasMembers
                    ? [...state.members].sort((a, b) => {
                        const roleWeights = { Leader: 3, Roller: 2, Member: 1 };
                        return (
                          (roleWeights[b.role] || 0) -
                          (roleWeights[a.role] || 0)
                        );
                      })
                    : [];

                  return (
                    <motion.li
                      layout
                      key={state.team_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        zIndex: dynamicZIndex,
                      }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileLayout={{
                        zIndex: 999,
                        scale: 1.02,
                        boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      }}
                      transition={{
                        layout: {
                          type: "spring",
                          stiffness: 100,
                          damping: 20,
                          mass: 1.2,
                        },
                      }}
                      className="team-position-item"
                      style={{
                        borderLeftColor: state.team_color,
                        position: "relative",
                      }}
                    >
                      <motion.div
                        layout="position"
                        className={`team-header ${hasMembers ? "has-members" : "empty-team"}`}
                        onClick={() =>
                          hasMembers && toggleTeamExpansion(state.team_id)
                        }
                        role="button"
                        aria-expanded={isExpanded}
                        style={{
                          height: "50px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <div className="team-header-left">
                          <span className="team-info-summary">
                            <strong style={{ color: state.team_color }}>
                              {state.team_name}
                            </strong>
                            <span className="team-stats-inline">
                              <span className="position-text">
                                Pos: <strong>{state.current_position}</strong>
                              </span>
                              <span
                                className={`revert-tag ${isAtRevertLimit ? "at-limit" : ""}`}
                              >
                                ({state.revert_count} /{" "}
                                {game.max_reverts_allowed} reverts)
                              </span>
                            </span>
                          </span>
                        </div>

                        <div className="team-header-right-group">
                          <span className="team-member-count">
                            {memberCount}{" "}
                            {memberCount === 1 ? "Member" : "Members"}
                          </span>
                          <div
                            className="expansion-indicator"
                            style={{ opacity: hasMembers ? 1 : 0.3 }}
                          >
                            {isExpanded && hasMembers ? (
                              <ChevronDown size={16} strokeWidth={3} />
                            ) : (
                              <ChevronRight
                                size={16}
                                strokeWidth={2}
                                opacity={0.6}
                              />
                            )}
                          </div>
                        </div>
                      </motion.div>

                      {hasMembers && (
                        <ul
                          className={`team-members-list ${!isExpanded ? "collapsed" : ""}`}
                        >
                          {sortedMembers.map((member, idx) => (
                            <li
                              key={idx}
                              className={`member-item role-${member.role.toLowerCase()}`}
                            >
                              <span className="member-role-indicator">
                                {member.role === "Leader" ? (
                                  <Crown
                                    size={14}
                                    className="role-icon leader"
                                  />
                                ) : member.role === "Roller" ? (
                                  <Dice5
                                    size={14}
                                    className="role-icon roller"
                                  />
                                ) : (
                                  <User
                                    size={14}
                                    className="role-icon member"
                                  />
                                )}
                              </span>
                              <span className="member-name">{member.ign}</span>
                              <span className="member-role-label">
                                {member.role}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  );
                })}
            </AnimatePresence>
          </ul>
        </div>

        <div className="game-log-container">
          <div className="log-header-row">
            <h3>
              Live Game Log&nbsp;
              <span className="utc-label">(in UTC time)</span>
            </h3>

            <div className="log-controls">
              <select
                className="log-filter-select"
                value={selectedFilterTeam}
                onChange={(e) => setSelectedFilterTeam(e.target.value)}
              >
                <option value="All">All Teams</option>
                {game.team_states.map((state) => (
                  <option key={state.team_id} value={state.team_name}>
                    {state.team_name}
                  </option>
                ))}
              </select>

              <label className="progression-toggle">
                <input
                  type="checkbox"
                  checked={showProgressionLines}
                  onChange={(e) => setShowProgressionLines(e.target.checked)}
                />
                Show Paths
              </label>
            </div>
          </div>

          <ul className="game-log">
            {filteredLogs
              .slice(-100)
              .reverse()
              .map((log, index) => (
                <li key={log.id || index} className="log-entry">
                  [{new Date(log.timestamp).toLocaleTimeString()}]&nbsp;
                  <strong
                    style={{
                      color:
                        log.team_name === "Server"
                          ? "#e67e22"
                          : log.team_name === "Admin"
                            ? "#f1c40f"
                            : log.team_color,
                    }}
                  >
                    {log.team_name}:
                  </strong>
                  &nbsp;{log.message}
                </li>
              ))}
          </ul>
        </div>
      </div>

      <Modal
        isOpen={showRollModal}
        onConfirm={confirmRoll}
        onCancel={() => setShowRollModal(false)}
        title="Confirm Dice Roll"
        message={rollModalContent}
        confirmButtonText={isRolling ? "Rolling..." : "Confirm & Roll"}
        confirmButtonClassName="btn-primary"
        isConfirmDisabled={isRolling}
      />

      <Modal
        isOpen={showRevertModal}
        onConfirm={confirmRevert}
        onCancel={() => setShowRevertModal(false)}
        title="Confirm Roll Reversion"
        message={revertModalContent}
        confirmButtonText={isReverting ? "Reverting..." : "Confirm Revert"}
        confirmButtonClassName="btn-danger-action"
        isConfirmDisabled={isReverting || !canRevert || !isLeader}
      />

      {showCopyTooltip && (
        <div className="copy-toast">
          <Copy size={16} />
          <span>Spectator link copied to clipboard!</span>
        </div>
      )}
    </div>
  );
}

export default GameDetail;
